import { cycles } from "@ap-frontend/questions"
import { Result } from "@ap-frontend/types"
import { z, ZodError } from "zod"

export const DEPLOYMENT_CONFIG_PATH = "/deployment-config.json"
export const LEGACY_FIREBASE_CONFIG_PATH = "/firebase-config.json"

/** UI configuration used when firebase-config.json omits a `uiConfig`. */
const DEFAULT_UI_CONFIG: UIConfig = { cycleYear: cycles.cycleYear }

export const EmulatorConfig = z.object({
  auth: z.string().optional(),
  firestore: z.string().optional(),
  storage: z.string().optional(),
})
export type EmulatorConfig = z.infer<typeof EmulatorConfig>

export const FirebaseConfig = z.object({
  apiKey: z.string(),
  appId: z.string(),
  authDomain: z.string(),
  storageBucket: z.string(),
  projectId: z.string(),
  emulators: EmulatorConfig.optional(),
  functionUrls: z.object({
    apEmailsProxy: z.url({ protocol: /^https?$/ }),
  }),
})
export type FirebaseConfig = z.infer<typeof FirebaseConfig>

export const UIConfig = z.object({
  cycleYear: z.number().int().min(2020),
})
export type UIConfig = z.infer<typeof UIConfig>

/** Environment-specific configuration for the MyCApp frontend. */
export const DeploymentConfig = z.object({
  firebaseConfig: FirebaseConfig,
  uiConfig: UIConfig,
})
export type DeploymentConfig = z.infer<typeof DeploymentConfig>

export function parseConfigJson(
  value: unknown,
  options: { defaultUIConfig: UIConfig },
): DeploymentConfig {
  return DeploymentConfig.or(FirebaseConfig)
    .pipe(
      z.transform(obj =>
        "firebaseConfig" in obj
          ? obj
          : { firebaseConfig: obj, uiConfig: options.defaultUIConfig },
      ),
    )
    .parse(value)
}

export type LoadDeploymentConfig = (options?: {
  signal?: AbortSignal
}) => Promise<LoadDeploymentConfigResult>

type DeploymentConfigErrorReason =
  | "not-found"
  | "parse-failed"
  | "request-failed"
interface DeploymentConfigErrorOptions extends ErrorOptions {
  reason: DeploymentConfigErrorReason
}

/** Thrown when loadDeploymentConfig() fails. */
export class DeploymentConfigError extends Error {
  readonly reason: DeploymentConfigErrorReason
  constructor(
    message: string,
    { reason, ...options }: DeploymentConfigErrorOptions,
  ) {
    super(message, options)
    this.reason = reason
  }
}

type LoadDeploymentConfigResultError = {
  reason: DeploymentConfigErrorReason
  cause: unknown
}
export type LoadDeploymentConfigResult = Result<
  DeploymentConfig,
  LoadDeploymentConfigResultError
>

export async function loadDeploymentConfig({
  path = DEPLOYMENT_CONFIG_PATH,
  signal,
}: {
  path?: string
  signal?: AbortSignal
} = {}): Promise<LoadDeploymentConfigResult> {
  try {
    const resp = await fetch(path, { signal })
    if (!resp.ok) {
      const reason = resp.status === 404 ? "not-found" : "request-failed"
      return { success: false, error: { reason, cause: resp } }
    }
    return {
      success: true,
      data: parseConfigJson(await resp.json(), {
        defaultUIConfig: DEFAULT_UI_CONFIG,
      }),
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e

    return {
      success: false,
      error: {
        reason: e instanceof ZodError ? "parse-failed" : "request-failed",
        cause: e,
      },
    }
  }
}
loadDeploymentConfig satisfies LoadDeploymentConfig

export async function loadDeploymentConfigFromOldOrNewPath({
  signal,
}: {
  signal?: AbortSignal
} = {}): Promise<LoadDeploymentConfigResult> {
  // Support loading from the original firebase-specific path, and the new, more
  // general path, to make deployment migration easier.
  // Favour the original location so that legacy deployments keep using the
  // old config until they explicitly remove it.
  const legacyConfig = await loadDeploymentConfig({
    path: LEGACY_FIREBASE_CONFIG_PATH,
    signal,
  })
  // Only try the new config location if the legacy config does not exist, so
  // that deployments have to explicitly opt in to the new one.
  if (legacyConfig.success || legacyConfig.error.reason !== "not-found")
    return legacyConfig

  return await loadDeploymentConfig({ signal })
}
loadDeploymentConfigFromOldOrNewPath satisfies LoadDeploymentConfig
